import {APPROVED_GATE_ORDER} from './panel.js';
import type {
  ContentEditorialDto,
  ContentMediaEditorialDto,
} from './editorial-review.js';
import type {PanelSnapshotDto} from './panel.js';

/**
 * FONTE ÚNICA do conteúdo ilustrativo do painel.
 *
 * Mora nos contratos, e não no painel, porque DOIS lados precisam dele: a tela,
 * enquanto não há API, e a API, enquanto os planos 04, 05 e 06 não trazem os
 * backends de MAPA, métricas e operação. Duas cópias divergiriam, e aí o painel
 * mostraria uma coisa em desenvolvimento e outra em produção, ambas rotuladas
 * como simuladas, sem ninguém entender a diferença.
 *
 * Reprodução literal dos textos e números da prévia aprovada
 * (`docs/ui-reference/painel-instagram-devmil/approved-source.html`).
 *
 * Isto é dado SIMULADO. Cada bloco carrega `illustrative: true` e a tela rotula
 * o que veio daqui. Nenhum valor prova estado externo: não há token, nem @, nem
 * telefone, nem e-mail, nem nome de pessoa.
 *
 * Horários: a prévia mostra horário de Brasília. `scheduledAt` guarda o instante
 * em UTC, e a tela formata em `America/Sao_Paulo`.
 */

/** UUID v4 determinístico, para a fixture não depender de sorteio. */
const uuid = (n: number): string =>
  `00000000-0000-4000-8000-${n.toString(16).padStart(12, '0')}`;

/**
 * `sha256` determinístico e reconhecivelmente ILUSTRATIVO.
 *
 * Ele passa na régua do contrato (64 hexadecimais minúsculos) e não é hash de
 * coisa nenhuma: o prefixo `0`s e o número no fim dizem, para quem olhar um
 * log, que aquele valor saiu daqui e não da selagem de um manifesto de verdade.
 * Calcular um sha256 real sobre texto de fixture seria pior: pareceria medição.
 */
const hashIlustrativo = (n: number): string =>
  `${'0'.repeat(58)}${n.toString(16).padStart(6, 'a')}`;

const SERIES = {
  impacto: {
    id: 'impacto-operacional',
    name: 'Impacto operacional',
    shortName: 'Impacto',
  },
  caderno: {
    id: 'caderno-de-automacao',
    name: 'Caderno de Automação',
    shortName: 'Caderno',
  },
  radar: {id: 'radar-ia', name: 'Radar IA', shortName: 'Radar IA'},
} as const;

const PREVIEW_BRAND = 'DEVMIL · AUTOMAÇÃO';
const PREVIEW_FOOTER = 'devmil.online · responda MAPA';

/**
 * Conteúdo simulado não tem arquivo renderizado, e por isso as quatro peças
 * declaram os dois campos em `false`.
 *
 * Isto não é detalhe de fixture: é o que mantém a prévia esquemática no ar
 * enquanto a fonte for simulada, e o que faz a suíte visual continuar medindo
 * exatamente o quadro aprovado. Uma fixture com `true` aqui apontaria a tela
 * para uma rota que, no modo simulado, não existe.
 */
const SEM_MIDIA = {cover: false, video: false, slides: 0} as const;

/**
 * A mídia de um CARROSSEL ilustrativo: sete slides, e nenhum arquivo.
 *
 * `slides: 7` sem `cover` nem `video` é o que a tela precisa para desenhar a
 * navegação da sequência (o "1 / 7", as setas, as miniaturas) sem tentar buscar
 * byte nenhum: a fonte simulada devolve `null` em `mediaUrl`, e o quadro cai na
 * prévia esquemática do slide.
 *
 * Ele é diferente de `SEM_MIDIA` de propósito. Com `slides: 0` a tela do
 * carrossel ilustrativo seria idêntica à de um Reel, e a suíte visual
 * congelaria um desenho que não existe.
 */
const CARROSSEL_SEM_ARQUIVO = {cover: false, video: false, slides: 7} as const;

/** Sete portões todos verdes. */
const allGatesPass = () =>
  APPROVED_GATE_ORDER.map((id) => ({id, outcome: 'pass' as const}));

/** Sete portões com `Originalidade` em alerta, como na peça selecionada. */
const gatesWithOriginalityAlert = () =>
  APPROVED_GATE_ORDER.map((id) => ({
    id,
    outcome: id === 'originality' ? ('alert' as const) : ('pass' as const),
  }));

/**
 * O PARECER ilustrativo da peça selecionada.
 *
 * Ele existe para a tela ter o que desenhar no bloco de parecer da revisão
 * individual, que é comparado pixel a pixel. Uma peça `unavailable` deixaria o
 * bloco sempre no estado curto, e o desenho cheio nunca seria medido.
 *
 * A recomendação é `rewrite` de propósito: `fit` desenharia o caso mais fácil, e
 * é justamente o caso em que o editor DISCORDA que precisa caber na tela sem
 * empurrar a decisão de quem aprova.
 *
 * Nada aqui é medição, e a tela rotula o bloco como simulado junto com o resto.
 */
const PARECER_ILUSTRATIVO: ContentEditorialDto = {
  availability: 'available',
  recommendation: 'rewrite',
  total: 14,
  meetsFloor: false,
  criteria: [
    {
      id: 'audience_relevance',
      score: 3,
      excerpt: 'Seu financeiro ainda copia dados?',
    },
    {id: 'hook_clarity', score: 3, excerpt: null},
    {
      id: 'practical_delivery',
      score: 2,
      excerpt: 'elimina retrabalho e reduz erros de digitação',
    },
    {id: 'promise_coherence', score: 3, excerpt: null},
    {id: 'format_fit', score: 3, excerpt: null},
  ],
  findings: [
    {
      field: 'caption',
      excerpt: 'elimina retrabalho e reduz erros de digitação',
      issue:
        'a peça diz o benefício e não mostra o procedimento: falta o passo que ' +
        'alguém consegue executar amanhã',
    },
  ],
  mainChange: 'trocar o benefício genérico por um passo concreto da rotina',
  pendency: null,
  corrected: false,
  correctionAttempts: 0,
  scope: 'text',
  rubricVersion: 'devmil-editorial-regua-2026-09-a',
  promptVersion: 'devmil-editorial-p05-2026-09-a',
  model: 'gpt-5.6-terra',
  evaluatedAt: '2026-08-27T14:20:00Z',
};

/**
 * O estado das outras peças: SEM parecer, com o motivo.
 *
 * `OFF` é o estado real de hoje, e a tela precisa saber desenhá-lo: o painel
 * nasce com a revisão editorial desligada, e um espaço em branco ali seria lido
 * como "está tudo certo" por quem aprova.
 */
const SEM_PARECER: ContentEditorialDto = {
  availability: 'unavailable',
  reason: 'OFF',
};

/**
 * O parecer da MÍDIA da peça ilustrativa, e por que ele mostra o caso PARCIAL.
 *
 * A cobertura é `cover`: uma imagem só, a capa do Reel, com o total de telas
 * ao lado. É o caso que a tela precisa desenhar certo, porque é o que mais
 * convida ao erro de leitura — um parecer favorável sobre a capa, lido como
 * parecer sobre a peça, é a nota favorável inventada que o plano proíbe.
 *
 * `videoReviewed: false` é literal no contrato: nenhum caminho desta esteira
 * assiste ao vídeo, e as amostras são quadros parados.
 */
const PARECER_VISUAL_ILUSTRATIVO: ContentMediaEditorialDto = {
  availability: 'available',
  recommendation: 'fit',
  total: 9,
  meetsFloor: true,
  criteria: [
    {id: 'silent_comprehension', score: 3, excerpt: 'FINANCEIRO NO PILOTO'},
    {id: 'reading_comfort', score: 3, excerpt: null},
    {id: 'story_coherence', score: 3, excerpt: null},
  ],
  findings: [
    {
      field: 'cover',
      excerpt: 'FINANCEIRO NO PILOTO',
      issue:
        'a capa entrega o assunto, e o número que sustenta a promessa só ' +
        'aparece na segunda tela',
    },
  ],
  mainChange: null,
  pendency: null,
  scope: 'media',
  coverage: {kind: 'cover', stills: 1, screens: 4, videoReviewed: false},
  rubricVersion: 'devmil-editorial-regua-visual-2026-09-a',
  promptVersion: 'devmil-editorial-visual-2026-09-b',
  model: 'gpt-5.6-terra',
  evaluatedAt: '2026-08-27T14:22:00Z',
};

/** O estado das outras peças: sem parecer visual, com o motivo. */
const SEM_PARECER_VISUAL: ContentMediaEditorialDto = {
  availability: 'unavailable',
  reason: 'OFF',
};

export const ILLUSTRATIVE_SNAPSHOT: PanelSnapshotDto = {
  operation: {
    environment: 'production',
    connection: 'connected',
    paused: false,
    configuredAutonomyLevel: 0,
    effectiveAutonomyLevel: 0,
  },

  today: {
    illustrative: true,
    date: '2026-08-27',
    // Lista vazia é MEDIÇÃO: o dia ilustrativo não tem alerta nenhum. Pôr um
    // alerta de exemplo aqui ensinaria a tela a desenhar um estado que a fonte
    // real quase nunca produz, e a captura visual congelaria esse estado.
    alerts: [],
    mix: {reels: 3, stories: 3, photos: 1, carousels: 1},
    // A fila mostra 6 peças; os contadores falam das 8 do dia. Por isso eles
    // não são derivados da lista visível. Oito desde 2026-09-06: 3 Reels, 3
    // Stories, o post de foto das 12:00 e o carrossel das 16:30.
    // Os sete contadores são DISJUNTOS e somam `generated`: 6 esperando
    // decisão, 1 liberada sem item de fila, 1 já publicada.
    counters: {
      generated: 8,
      awaiting: 6,
      released: 1,
      scheduled: 0,
      published: 1,
      expired: 0,
      failed: 0,
      closed: 0,
    },
    nextSlot: {
      scheduledAt: '2026-08-27T17:30:00Z',
      kind: 'reel',
      status: 'valid',
    },
    queue: [
      {
        id: uuid(1),
        contentVersionId: uuid(101),
        contentItemId: uuid(201),
        kind: 'reel',
        series: SERIES.impacto,
        title: 'Seu financeiro ainda copia dados?',
        caption:
          'Mostra como uma integração entre plataformas elimina retrabalho e reduz erros de digitação.',
        scheduledAt: '2026-08-27T17:30:00Z',
        localDate: '2026-08-27',
        status: 'awaiting_approval',
        gateResults: gatesWithOriginalityAlert(),
        preview: {
          brand: PREVIEW_BRAND,
          hook: 'SEU FINANCEIRO AINDA COPIA DADOS?',
          footer: PREVIEW_FOOTER,
        },
        media: SEM_MIDIA,
        editorial: PARECER_ILUSTRATIVO,
        mediaEditorial: PARECER_VISUAL_ILUSTRATIVO,
        approval: {kind: 'ready', manifestHash: hashIlustrativo(101)},
        publication: {state: 'pending_release'},
      },
      {
        id: uuid(2),
        contentVersionId: uuid(102),
        contentItemId: uuid(202),
        kind: 'story',
        series: SERIES.caderno,
        title: '3 tarefas que a IA pode organizar',
        caption:
          'Story educativo com três sinais de que uma rotina repetitiva já pode virar automação.',
        scheduledAt: '2026-08-27T19:00:00Z',
        localDate: '2026-08-27',
        status: 'awaiting_approval',
        gateResults: allGatesPass(),
        preview: {
          brand: PREVIEW_BRAND,
          hook: '3 TAREFAS QUE A IA PODE ORGANIZAR',
          footer: PREVIEW_FOOTER,
        },
        media: SEM_MIDIA,
        editorial: SEM_PARECER,
        mediaEditorial: SEM_PARECER_VISUAL,
        approval: {kind: 'ready', manifestHash: hashIlustrativo(102)},
        publication: {state: 'pending_release'},
      },
      {
        id: uuid(3),
        contentVersionId: uuid(103),
        contentItemId: uuid(203),
        kind: 'reel',
        series: SERIES.radar,
        title: 'Agente de IA não é chatbot',
        caption:
          'Explica, em linguagem simples, quando um agente consulta sistemas e executa tarefas de verdade.',
        scheduledAt: '2026-08-27T21:30:00Z',
        localDate: '2026-08-27',
        status: 'approved',
        gateResults: allGatesPass(),
        preview: {
          brand: PREVIEW_BRAND,
          hook: 'AGENTE DE IA NÃO É CHATBOT',
          footer: PREVIEW_FOOTER,
        },
        media: SEM_MIDIA,
        editorial: SEM_PARECER,
        mediaEditorial: SEM_PARECER_VISUAL,
        // A peça JÁ decidida não é aprovável por esta tela, e `decided` diz
        // isso. Um hash aqui convidaria um segundo clique sobre o que já foi
        // liberado; o `null` de antes dizia a mesma coisa que "não deu para
        // selar", que é defeito e pede outra reação.
        approval: {kind: 'decided'},
        publication: {state: 'published'},
      },
      {
        id: uuid(4),
        contentVersionId: uuid(104),
        contentItemId: uuid(204),
        kind: 'story',
        series: SERIES.impacto,
        title: 'Onde sua operação perde tempo?',
        caption:
          'Convite visual para o empresário identificar um gargalo e responder MAPA no Direct.',
        scheduledAt: '2026-08-27T23:00:00Z',
        localDate: '2026-08-27',
        status: 'awaiting_approval',
        gateResults: allGatesPass(),
        preview: {
          brand: PREVIEW_BRAND,
          hook: 'ONDE SUA OPERAÇÃO PERDE TEMPO?',
          footer: PREVIEW_FOOTER,
        },
        media: SEM_MIDIA,
        editorial: SEM_PARECER,
        mediaEditorial: SEM_PARECER_VISUAL,
        approval: {kind: 'ready', manifestHash: hashIlustrativo(104)},
        publication: {state: 'pending_release'},
      },
      {
        /*
         * O POST DE FOTO das 12:00, que entrou em 2026-09-05.
         *
         * Ele está na fila ilustrativa porque a tela precisa saber desenhá-lo:
         * ícone próprio, rótulo "Foto" e um quadro que mostra a imagem em vez
         * do esquema. Sem ele aqui, a única forma de ver o desenho seria contra
         * dado real, e a suíte visual nunca o congelaria.
         *
         * `media: SEM_MIDIA` como as outras: dado indisponível não vira "tem", e
         * a fixture não tem arquivo nenhum para servir. A tela cai na prévia
         * esquemática, que é o que ela mostra em rascunho.
         */
        id: uuid(5),
        contentVersionId: uuid(105),
        contentItemId: uuid(205),
        kind: 'photo',
        series: SERIES.radar,
        title: 'Integração nova entre plataformas de mensagem',
        caption:
          'Legenda ilustrativa de post de foto: o que foi anunciado, por que ' +
          'importa para um pequeno negócio, o que dá para fazer nesta semana, ' +
          'e a fonte citada por nome.',
        /*
         * 20:30, e não 12:00, e é escolha deliberada.
         *
         * Na operação de verdade o item de notícia é o das 12:00, e a fila o
         * mostra como o segundo bloco do dia. Esta fila é ILUSTRATIVA e os
         * horários dela nunca foram os de produção (14:30, 16:00, 18:30 e
         * 20:00, contra 09:00, 14:00 e 19:00): o que ela existe para provar é o
         * DESENHO da peça de foto, e pô-la no fim mantém a peça selecionada por
         * padrão sendo a mesma de sempre.
         *
         * Trocar a seleção padrão mudaria a coluna inteira de revisão no
         * baseline visual, e o que mudou de verdade nesta revisão é um bloco a
         * mais na fila.
         */
        scheduledAt: '2026-08-27T23:30:00Z',
        localDate: '2026-08-27',
        status: 'awaiting_approval',
        gateResults: allGatesPass(),
        preview: {
          brand: PREVIEW_BRAND,
          hook: 'INTEGRAÇÃO NOVA ENTRE PLATAFORMAS',
          footer: PREVIEW_FOOTER,
        },
        media: SEM_MIDIA,
        editorial: SEM_PARECER,
        mediaEditorial: SEM_PARECER_VISUAL,
        approval: {kind: 'ready', manifestHash: hashIlustrativo(105)},
        publication: {state: 'pending_release'},
      },
      {
        /*
         * O CARROSSEL das 16:30, que entrou em 2026-09-06.
         *
         * Ele está na fila ilustrativa pelo mesmo motivo do post de foto: a
         * tela precisa saber desenhá-lo, e o desenho dele é o mais diferente
         * dos quatro — não é um quadro só, é uma SEQUÊNCIA com navegação,
         * contador e miniaturas. Sem ele aqui, a única forma de ver esse
         * desenho seria contra dado real, e a suíte visual nunca o congelaria.
         *
         * `CARROSSEL_SEM_ARQUIVO`, e não `SEM_MIDIA`: sete slides declarados e
         * nenhum arquivo servível. É o que faz a tela desenhar a navegação da
         * sequência sobre a prévia esquemática, que é o estado de uma peça em
         * rascunho.
         */
        id: uuid(6),
        contentVersionId: uuid(106),
        contentItemId: uuid(206),
        kind: 'carousel',
        series: SERIES.caderno,
        title: 'Cinco ajustes que devolvem a segunda-feira da equipe',
        caption:
          'Legenda ilustrativa de carrossel: salve para consultar quando a ' +
          'semana apertar. São cinco ajustes que cabem numa manhã.',
        /*
         * 21:00, e não 16:30, pelo mesmo motivo do post de foto: esta fila é
         * ILUSTRATIVA e os horários dela nunca foram os de produção. Pôr o
         * carrossel no fim mantém a peça selecionada por padrão sendo a mesma
         * de sempre, e o que muda no baseline visual é um bloco a mais na fila.
         */
        scheduledAt: '2026-08-28T00:00:00Z',
        localDate: '2026-08-27',
        status: 'awaiting_approval',
        gateResults: allGatesPass(),
        preview: {
          brand: PREVIEW_BRAND,
          hook: 'CINCO AJUSTES QUE DEVOLVEM A SEGUNDA-FEIRA',
          footer: PREVIEW_FOOTER,
        },
        media: CARROSSEL_SEM_ARQUIVO,
        editorial: SEM_PARECER,
        mediaEditorial: SEM_PARECER_VISUAL,
        approval: {kind: 'ready', manifestHash: hashIlustrativo(106)},
        publication: {state: 'pending_release'},
      },
    ],
  },

  mapa: {
    illustrative: true,
    stages: [
      {id: 'started', count: 34},
      {id: 'diagnosed', count: 21},
      {id: 'whatsapp', count: 12},
      {id: 'meeting', count: 4},
    ],
    conversations: [
      {
        reference: 'lead_a7f2',
        stage: 'diagnosed',
        diagnostic: {answered: 5, total: 5},
        origin: {channel: 'reel', label: 'Integração'},
        window: {kind: 'countdown', hoursRemaining: 18},
        nextAction: 'await_whatsapp',
      },
      {
        reference: 'lead_91bc',
        stage: 'whatsapp',
        diagnostic: null,
        origin: {channel: 'story', label: 'MAPA'},
        window: {kind: 'associated'},
        nextAction: 'redeem_code',
      },
      {
        reference: 'lead_3d10',
        stage: 'started',
        diagnostic: {answered: 2, total: 5},
        origin: {channel: 'instagram', label: null},
        window: {kind: 'countdown', hoursRemaining: 7},
        nextAction: 'await_reply',
      },
      {
        reference: 'lead_f048',
        stage: 'meeting',
        diagnostic: null,
        origin: {channel: 'reel', label: 'Agentes'},
        window: {kind: 'completed'},
        nextAction: 'commercial_event',
      },
    ],
    /*
     * As oito capacidades, TODAS desligadas.
     *
     * Este bloco nao e ilustrativo como o resto do arquivo, e por isso ele nao
     * inventa nada: capacidade e fato do processo, e a fixture roda em processo
     * nenhum. Todo desligado e a leitura de quem nao declarou fiacao, que e o
     * mesmo padrao de `FIACAO_NAO_DECLARADA` no dominio. Ausencia nunca e
     * permissiva.
     *
     * Em producao a lista e substituida pela medida, capacidade a capacidade,
     * em `montarMapa`. Este arranjo tambem e o que a referencia visual congela,
     * e nao por acaso: em 07/09/2026 a fiacao real tem no maximo duas das oito
     * ligadas, e a tela precisa saber desenhar a linha desligada.
     */
    capabilities: [
      {
        capability: 'webhook_ingress',
        active: false,
        reason: 'NAO_CONFIGURADA',
      },
      {capability: 'transicao', active: false, reason: 'NAO_LIGADA'},
      {
        capability: 'aviso_de_privacidade',
        active: false,
        reason: 'SEM_APROVACAO_DO_DONO',
      },
      {
        capability: 'enfileiramento_de_envio',
        active: false,
        reason: 'NAO_LIGADA',
      },
      {capability: 'envio_de_mensagem', active: false, reason: 'NAO_LIGADA'},
      {
        capability: 'link_de_whatsapp',
        active: false,
        reason: 'NAO_IMPLEMENTADA',
      },
      {capability: 'resgate_manual', active: false, reason: 'NAO_IMPLEMENTADA'},
      {
        capability: 'redirecionador_de_campanha',
        active: false,
        reason: 'NAO_CONFIGURADA',
      },
    ],
  },

  metrics: {
    illustrative: true,
    windowDays: 30,
    reach: {
      availability: 'available',
      value: 18_400,
      measuredWindowDays: 30,
      measuredAt: '2026-08-28T06:10:00Z',
      sourceRevision: 'v23.0',
      sample: null,
    },
    saves: {
      availability: 'available',
      value: 436,
      measuredWindowDays: 30,
      measuredAt: '2026-08-28T06:10:00Z',
      sourceRevision: 'v23.0',
      sample: {measured: 7, eligible: 7},
    },
    savesRateOfReach: {availability: 'available', value: 2.37},
    mapaStarted: {availability: 'available', value: 34},
    // Receita atribuída não foi medida. Não vira zero.
    attributedRevenue: {
      availability: 'unavailable',
      currency: 'BRL',
      cents: null,
    },
    coverage: {
      lastMeasuredAt: '2026-08-28T06:10:00Z',
      publishedPieces: 7,
      measuredPieces: 7,
      piecesWithoutFormat: 0,
    },
    // A comparação por formato e idade, que é o que orienta pauta. O carrossel
    // aparece SEM amostra de propósito: a esteira ainda não publica carrossel,
    // e a linha vazia com motivo é mais informativa que a linha ausente.
    formatPerformance: [
      {
        format: 'reel',
        ageDays: 1,
        metric: 'reach',
        average: {
          availability: 'available',
          value: 1_240,
          measuredWindowDays: 1,
          measuredAt: '2026-08-28T06:10:00Z',
          sourceRevision: 'v23.0',
          sample: {measured: 3, eligible: 3},
        },
      },
      {
        format: 'story',
        ageDays: 1,
        metric: 'reach',
        average: {
          availability: 'available',
          value: 410,
          measuredWindowDays: 1,
          measuredAt: '2026-08-28T06:10:00Z',
          sourceRevision: 'v23.0',
          sample: {measured: 3, eligible: 3},
        },
      },
      {
        format: 'photo',
        ageDays: 1,
        metric: 'reach',
        average: {
          availability: 'unavailable',
          value: null,
          gap: 'insufficient_sample',
          instead: null,
          sample: {measured: 1, eligible: 1},
        },
      },
      {
        format: 'carousel',
        ageDays: 1,
        metric: 'reach',
        average: {
          availability: 'unavailable',
          value: null,
          gap: 'never_collected',
          instead: null,
          sample: {measured: 0, eligible: 0},
        },
      },
    ],
    series: [
      {
        series: SERIES.impacto,
        reach: {
          availability: 'available',
          value: 1_180,
          measuredWindowDays: 30,
          measuredAt: '2026-08-28T06:10:00Z',
          sourceRevision: 'v23.0',
          sample: {measured: 3, eligible: 3},
        },
      },
      {
        series: SERIES.radar,
        reach: {
          availability: 'available',
          value: 890,
          measuredWindowDays: 30,
          measuredAt: '2026-08-28T06:10:00Z',
          sourceRevision: 'v23.0',
          sample: {measured: 2, eligible: 2},
        },
      },
      {
        series: SERIES.caderno,
        reach: {
          availability: 'unavailable',
          value: null,
          gap: 'insufficient_sample',
          instead: null,
          sample: {measured: 1, eligible: 2},
        },
      },
    ],
    candidates: [
      {
        id: uuid(201),
        title: 'Integração sem retrabalho',
        format: 'reel',
        metric: 'reach',
        value: 2_100,
        ageDays: 1,
        measuredAt: '2026-08-28T06:10:00Z',
        sourceRevision: 'v23.0',
        cohortAverage: 1_240,
        cohortSize: 3,
      },
      {
        id: uuid(202),
        title: 'Agente consulta o ERP',
        format: 'story',
        metric: 'reach',
        value: 640,
        ageDays: 1,
        measuredAt: '2026-08-28T06:10:00Z',
        sourceRevision: 'v23.0',
        cohortAverage: 410,
        cohortSize: 3,
      },
    ],
  },

  operationDashboard: {
    illustrative: true,
    levels: [
      {
        level: 0,
        description: 'Todas as 7 peças passam por aprovação individual',
        eligibility: {kind: 'active'},
      },
      {
        level: 1,
        description: 'Caderno e Radar elegíveis podem seguir por política',
        eligibility: {kind: 'after_days', days: 30},
      },
      {
        level: 2,
        description: 'Publicação monitorada dentro das políticas aprovadas',
        eligibility: {kind: 'after_days', days: 60},
      },
    ],
    health: {
      metaToken: 'healthy',
      publishQueue: {uncertain: 0},
      webhooks: 'healthy',
      // 02:10 em Brasília.
      lastVerifiedBackupAt: '2026-08-27T05:10:00Z',
      nextRestoreDrillInDays: 18,
    },
  },
};
